import { useMemo } from 'react'
import { Plus, ShoppingCart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatRelativeDate } from '@/utils/dateUtils'
import QuickOrderCard from './QuickOrderCard'

const FILTER_TABS = [
  { key: 'actionable', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'all', label: 'All' }
]

export default function QuickOrderList({ orders, filter, onFilterChange, onAdd, onEdit, loading }) {
  // Group orders by date
  const groupedOrders = useMemo(() => {
    const groups = {}
    for (const order of orders) {
      const date = order.date
      if (!groups[date]) {
        groups[date] = { date, label: formatRelativeDate(date), orders: [] }
      }
      groups[date].orders.push(order)
    }
    // Sort groups: most recent first
    return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [orders])

  return (
    <div className="flex flex-col h-full">
      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto px-1 pb-3 -mx-1 scrollbar-hide">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => onFilterChange({ ...filter, status: tab.key })}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
              'border',
              filter.status === tab.key
                ? 'bg-foreground text-background border-foreground'
                : 'bg-background text-muted-foreground border-border hover:bg-accent'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Order list */}
      <div className="flex-1 -mx-6 overflow-y-auto">
        {loading ? (
          <div className="space-y-3 px-6 py-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : groupedOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <ShoppingCart className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No orders in this period.</p>
            <p className="text-xs text-muted-foreground mt-1">Tap + to add one.</p>
          </div>
        ) : (
          groupedOrders.map(group => (
            <div key={group.date}>
              <div className="px-6 py-2 bg-muted/30 sticky top-0 z-10">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {group.label}
                </span>
              </div>
              <div>
                {group.orders.map(order => (
                  <QuickOrderCard
                    key={order.id}
                    order={order}
                    onClick={onEdit}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* FAB */}
      <div className="fixed bottom-6 right-6 z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <Button
          size="icon"
          onClick={onAdd}
          className="h-14 w-14 rounded-full shadow-lg bg-green-700 hover:bg-green-800"
        >
          <Plus className="h-6 w-6 text-white" />
        </Button>
      </div>
    </div>
  )
}
